/* eslint-disable no-nested-ternary */
import { ChangeEvent, useState } from 'react';
import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { OutboundProcess } from '@features/supplyChain/types';
import { useReactHookFormWithFieldArray } from '@hooks';
import { nowLocalDate } from '@utils';
import { PalletCalculator } from '@components/PalletCalculator';
import { PackagingBreakdown } from '@components/PackagingBreakdown';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import {
  ConfirmPickingNoteFormSku,
  ConfirmPickingNoteNoteFormValues,
} from './types';
import {
  makeDefaultValues,
  needsDiscrepancyReason,
  pickDiffersFromThePlan,
  productsPerSkuOf,
  validationSchema,
} from './form';
import { Datepicker } from '../../../tailwindComponents/Datepicker/Datepicker';
import { Input } from '../../../tailwindComponents/Input/Input';
import { Textarea } from '../../../tailwindComponents/Textarea/Textarea';
import { Button } from '../../../tailwindComponents/Button/Button';

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='10' />
      <line x1='15' x2='9' y1='9' y2='15' />
      <line x1='9' x2='15' y1='9' y2='15' />
    </svg>
  );
}

interface PlannedPickSummaryProps {
  wholeUnitsToPick: number;
  unitsToBreak: number;
  looseToTake: number;
}

function PlannedPickSummary({
  wholeUnitsToPick,
  unitsToBreak,
  looseToTake,
}: PlannedPickSummaryProps) {
  return (
    <span className='text-xs text-gray-500'>
      Plan: {numberToLocale(wholeUnitsToPick)} whole
      {unitsToBreak > 0
        ? ` + crack ${numberToLocale(unitsToBreak)} → ${numberToLocale(
            looseToTake
          )} loose`
        : ''}
    </span>
  );
}

const unitNounOf = (unitOfMeasure?: string) =>
  unitOfMeasure && unitOfMeasure.toLowerCase() !== 'other'
    ? unitOfMeasure
    : 'SKU';

const productsLabel = (quantity: number) =>
  `${numberToLocale(quantity)} products`;

const skuLineQuantityLabel = (
  quantity: number,
  sku: Pick<ConfirmPickingNoteFormSku, 'unitOfMeasure' | 'amountOfProductInSku'>
) => {
  const units = `${numberToLocale(quantity)} ${unitNounOf(sku.unitOfMeasure)}`;
  const productsPerSku = productsPerSkuOf(sku);
  return productsPerSku > 1
    ? `${units} · ${productsLabel(quantity * productsPerSku)}`
    : units;
};

const quantityColumnLabel = (
  quantity: 'Ordered' | 'Picked',
  {
    hasPlannedRows,
    hasSkuLineRows,
  }: { hasPlannedRows: boolean; hasSkuLineRows: boolean }
) => {
  if (hasPlannedRows && hasSkuLineRows) {
    return quantity;
  }
  return hasPlannedRows ? `${quantity} products` : `${quantity} SKUs`;
};

interface ConfirmPickingNoteFormProps {
  selectedOutboundProcess: OutboundProcess;
  onClose: () => void;
  onSubmit: (values: ConfirmPickingNoteNoteFormValues) => void;
  isDesktop: boolean;
  isTablet: boolean;
}
export function ConfirmPickingNoteForm({
  selectedOutboundProcess,
  onClose,
  onSubmit,
  isDesktop,
  isTablet,
}: ConfirmPickingNoteFormProps) {
  const {
    handleSubmit,
    control,
    setValue,
    isSubmitting,
    isValid,
    fields: skusFields,
    watch,
    errors,
  } = useReactHookFormWithFieldArray<ConfirmPickingNoteNoteFormValues>({
    defaultValues: makeDefaultValues(selectedOutboundProcess),
    validationSchema,
    fieldArrayName: 'skus',
  });

  const [deviationRowIds, setDeviationRowIds] = useState<Set<string>>(
    new Set()
  );
  const toggleDeviationRow = (rowId: string) => {
    setDeviationRowIds((current) => {
      const next = new Set(current);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const isPlannedRow = (field: (typeof skusFields)[number]) =>
    Boolean((field as typeof field & ConfirmPickingNoteFormSku).isPlannedLine);
  const hasPlannedRows = skusFields.some(isPlannedRow);
  const hasSkuLineRows = skusFields.some((field) => !isPlannedRow(field));
  const orderedColumnLabel = quantityColumnLabel('Ordered', {
    hasPlannedRows,
    hasSkuLineRows,
  });
  const pickedColumnLabel = quantityColumnLabel('Picked', {
    hasPlannedRows,
    hasSkuLineRows,
  });

  if (!selectedOutboundProcess || !selectedOutboundProcess.despatchNote) {
    return null;
  }

  const { despatchNote } = selectedOutboundProcess;
  const pickingNoteNumber = despatchNote.despatchNoteNumber.replace(
    '-DN',
    '-P'
  );

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(pickingNoteNumber);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            Picking note #{pickingNoteNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label='Copy picking note number'
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Shipper name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Shipper name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {selectedOutboundProcess.userBusinessName}
          </span>
        </div>

        {/* Required ETA */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Required ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {`${dayjs(despatchNote.requiredTimeOfArrival.dateTime)
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('D MMM YYYY')} · by ${dayjs(
              despatchNote.requiredTimeOfArrival.dateTime
            )
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('HH:mm')}`}
          </span>
        </div>

        {/* Recipient name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Recipient name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {despatchNote.customer?.name ?? despatchNote.customerName ?? 'N/A'}
          </span>
        </div>

        {/* Picking Date - Editable */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500 mb-1'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Picking Date</span>
          </div>
          <Controller
            name='pickingDateConfirmation'
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <Datepicker
                  name={field.name}
                  value={
                    field.value
                      ? dayjs(field.value).format('YYYY-MM-DD')
                      : dayjs(despatchNote.requiredPickingDate.dateTime).format(
                          'YYYY-MM-DD'
                        )
                  }
                  onChange={(e) => {
                    setValue('pickingDateConfirmation', dayjs(e.target.value), {
                      shouldValidate: true,
                    });
                  }}
                  endDate={nowLocalDate()}
                  isInvalid={!!error}
                  validationMessage={error?.message}
                />
              );
            }}
          />
        </div>
      </div>

      {/* SKU Table */}
      <div className='px-6 py-4'>
        {/* Table Header - Desktop only */}
        {isDesktop && (
          <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1.5fr_1fr_1fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
            <span className='text-xs font-semibold text-gray-500 text-center'>
              SKU #
            </span>
            <span className='text-xs font-semibold text-gray-500'>SKU ID</span>
            <span className='text-xs font-semibold text-gray-500'>
              Product name
            </span>
            <span className='text-xs font-semibold text-gray-500'>Pallets</span>
            <span className='text-xs font-semibold text-gray-500'>
              {orderedColumnLabel}
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              {pickedColumnLabel}
            </span>
          </div>
        )}

        {/* SKU Rows */}
        <div className='flex flex-col gap-6'>
          {skusFields.map((rowField, index) => {
            const skusField = rowField as typeof rowField &
              ConfirmPickingNoteFormSku;
            const skuQuantity = watch(`skus.${index}.skuQuantity`);
            const pickedQuantity = watch(`skus.${index}.pickedQuantity`);
            const isPlannedLine = skusField.isPlannedLine ?? false;
            const showDeviation = deviationRowIds.has(skusField.id);
            const productFactor = productsPerSkuOf(skusField);
            const wholeUnitsPicked = watch(`skus.${index}.wholeUnitsPicked`);
            const unitsBroken = watch(`skus.${index}.unitsBroken`);
            const looseTaken = watch(`skus.${index}.looseTaken`);
            const productsPicked = watch(`skus.${index}.productsPicked`);
            const plannedProductsToShip = skusField.plannedProductsToShip ?? 0;
            const isDiscrepancy = pickDiffersFromThePlan({
              isPlannedLine,
              skuQuantity,
              pickedQuantity,
              plannedProductsToShip,
              productsPicked,
            });
            const isReasonRequired = needsDiscrepancyReason({
              skuQuantity,
              pickedQuantity,
            });
            const plannedUnitsOverPlanMessage = isPlannedLine
              ? errors.skus?.[index]?.pickedQuantity?.message
              : undefined;
            const rowKind = {
              hasPlannedRows: isPlannedLine,
              hasSkuLineRows: !isPlannedLine,
            };

            const { packagingDetails } = skusField;
            const packagingInfo = packagingDetails?.packagingInfo ?? null;
            const palletisingType = packagingDetails?.palletisingType ?? null;
            const looseProducts = Math.max(
              0,
              (productsPicked ?? 0) - (wholeUnitsPicked ?? 0) * productFactor
            );
            const hasBreakdown =
              (skusField.packaging?.length ?? 0) > 0 || looseProducts > 0;

            const applyBreakPackChange = (
              nextWhole: number,
              nextBroken: number,
              nextLoose: number
            ) => {
              setValue(`skus.${index}.pickedQuantity`, nextWhole + nextBroken, {
                shouldValidate: true,
              });
              const balancedProducts =
                nextWhole * productFactor +
                nextBroken * productFactor +
                nextLoose;
              setValue(`skus.${index}.productsPicked`, balancedProducts, {
                shouldValidate: true,
              });
              if (
                !pickDiffersFromThePlan({
                  isPlannedLine,
                  skuQuantity,
                  pickedQuantity: nextWhole + nextBroken,
                  plannedProductsToShip,
                  productsPicked: balancedProducts,
                })
              ) {
                setValue(`skus.${index}.discrepancyReason`, null);
              }
            };

            const clearReasonWhenProductsMatchThePlan = (
              nextProductsPicked: number
            ) => {
              if (
                !pickDiffersFromThePlan({
                  isPlannedLine,
                  skuQuantity,
                  pickedQuantity,
                  plannedProductsToShip,
                  productsPicked: nextProductsPicked,
                })
              ) {
                setValue(`skus.${index}.discrepancyReason`, null);
              }
            };

            const revertRowToPlan = () => {
              setValue(
                `skus.${index}.wholeUnitsPicked`,
                skusField.wholeUnitsPicked ?? 0,
                { shouldValidate: true }
              );
              setValue(
                `skus.${index}.unitsBroken`,
                skusField.unitsBroken ?? 0,
                { shouldValidate: true }
              );
              setValue(`skus.${index}.looseTaken`, skusField.looseTaken ?? 0, {
                shouldValidate: true,
              });
              applyBreakPackChange(
                skusField.wholeUnitsPicked ?? 0,
                skusField.unitsBroken ?? 0,
                skusField.looseTaken ?? 0
              );
            };

            const handleDeviationToggle = () => {
              if (showDeviation) {
                revertRowToPlan();
              }
              toggleDeviationRow(skusField.id);
            };

            const deviationToggle = (
              <button
                type='button'
                className='text-sm font-medium text-primary hover:underline'
                onClick={handleDeviationToggle}
                aria-expanded={showDeviation}
              >
                {showDeviation ? 'Cancel deviation' : 'Report a deviation'}
              </button>
            );

            return (
              <div
                key={skusField.id}
                className={`${
                  index !== skusFields.length - 1
                    ? 'pb-6 border-b border-gray-100'
                    : ''
                }`}
              >
                {isDesktop ? (
                  /* Desktop Layout */
                  <div className='flex flex-col gap-4'>
                    <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1.5fr_1fr_1fr] gap-4 items-start'>
                      {/* Index */}
                      <div className='flex justify-center'>
                        <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                          {index + 1}
                        </span>
                      </div>
                      {/* SKU ID */}
                      <span className='text-sm text-gray-900'>
                        {skusField.skuIdentifier}
                      </span>
                      {/* Product name */}
                      <div className='flex flex-col'>
                        <span className='text-sm text-gray-900'>
                          {skusField.productName || skusField.skuName}
                        </span>
                        {packagingInfo ? (
                          <span className='text-xs text-gray-500'>
                            {packagingInfo.type}
                          </span>
                        ) : (
                          palletisingType && (
                            <span className='text-xs text-gray-500'>
                              {palletisingType}
                            </span>
                          )
                        )}
                        {isPlannedLine && (
                          <PlannedPickSummary
                            wholeUnitsToPick={skusField.wholeUnitsPicked ?? 0}
                            unitsToBreak={skusField.unitsBroken ?? 0}
                            looseToTake={skusField.looseTaken ?? 0}
                          />
                        )}
                        {isPlannedLine && (
                          <div className='mt-1'>{deviationToggle}</div>
                        )}
                      </div>
                      {/* Pallets */}
                      <div className='flex flex-col'>
                        {packagingInfo ? (
                          <span className='text-sm text-gray-900'>
                            <PalletCalculator
                              palletName={packagingInfo.type}
                              skuQuantity={skuQuantity}
                              amountOfSkusInLayer={
                                packagingInfo.amountOfSkusInLayer
                              }
                              amountOfLayersInPallet={
                                packagingInfo.amountOfLayersInPallet
                              }
                            />
                          </span>
                        ) : isPlannedLine && hasBreakdown ? (
                          <PackagingBreakdown
                            packaging={skusField.packaging}
                            looseProducts={looseProducts}
                          />
                        ) : (
                          <span className='text-sm text-gray-900'>
                            {palletisingType}
                          </span>
                        )}
                      </div>
                      {/* Ordered products */}
                      {isPlannedLine ? (
                        <div className='flex flex-col'>
                          <span className='text-sm text-gray-900'>
                            {productsLabel(plannedProductsToShip)}
                          </span>
                          <span className='text-xs text-gray-500'>
                            {numberToLocale(skuQuantity)} whole
                          </span>
                        </div>
                      ) : (
                        <span className='text-sm text-gray-900'>
                          {skuLineQuantityLabel(skuQuantity, skusField)}
                        </span>
                      )}
                      {/* Picked products */}
                      {isPlannedLine ? (
                        <div className='flex flex-col pt-1.5'>
                          <span className='text-sm font-medium text-gray-900'>
                            {productsLabel(productsPicked ?? 0)}
                          </span>
                          <span className='text-xs text-gray-500'>
                            {numberToLocale(pickedQuantity ?? 0)} whole
                          </span>
                        </div>
                      ) : (
                        <div className='flex flex-col gap-1'>
                          <Controller
                            name={`skus.${index}.pickedQuantity`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => {
                              return (
                                <Input
                                  name={field.name}
                                  type='number'
                                  value={field.value?.toString() ?? ''}
                                  onChange={(
                                    event: ChangeEvent<HTMLInputElement>
                                  ) => {
                                    const value = Number(event.target.value);
                                    if (Number.isNaN(value)) return;
                                    field.onChange(value);
                                    if (value === skuQuantity) {
                                      setValue(
                                        `skus.${index}.discrepancyReason`,
                                        null
                                      );
                                    }
                                  }}
                                  onBlur={field.onBlur}
                                  isInvalid={Boolean(error && isTouched)}
                                  validationMessage={error?.message}
                                  sizeVariant='sm'
                                />
                              );
                            }}
                          />
                          <span className='text-xs text-gray-500'>
                            {skuLineQuantityLabel(
                              pickedQuantity ?? 0,
                              skusField
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Break-pack capture row */}
                    {isPlannedLine && showDeviation && (
                      <div className='grid grid-cols-[2.5rem_1fr] gap-4'>
                        <div />
                        <div className='grid grid-cols-2 gap-4 max-w-2xl md:grid-cols-4'>
                          <Controller
                            name={`skus.${index}.wholeUnitsPicked`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                label='Whole units picked'
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = Number(event.target.value);
                                  if (Number.isNaN(value)) return;
                                  field.onChange(value);
                                  applyBreakPackChange(
                                    value,
                                    unitsBroken ?? 0,
                                    looseTaken ?? 0
                                  );
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                          <Controller
                            name={`skus.${index}.unitsBroken`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                label='Units broken'
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = Number(event.target.value);
                                  if (Number.isNaN(value)) return;
                                  field.onChange(value);
                                  applyBreakPackChange(
                                    wholeUnitsPicked ?? 0,
                                    value,
                                    looseTaken ?? 0
                                  );
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                          <Controller
                            name={`skus.${index}.looseTaken`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                label='Loose taken'
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = Number(event.target.value);
                                  if (Number.isNaN(value)) return;
                                  field.onChange(value);
                                  applyBreakPackChange(
                                    wholeUnitsPicked ?? 0,
                                    unitsBroken ?? 0,
                                    value
                                  );
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                          <Controller
                            name={`skus.${index}.productsPicked`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                label='Products picked'
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = Number(event.target.value);
                                  if (Number.isNaN(value)) return;
                                  field.onChange(value);
                                  clearReasonWhenProductsMatchThePlan(value);
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                        </div>
                        {plannedUnitsOverPlanMessage && (
                          <p className='col-start-2 text-sm text-red-600'>
                            {plannedUnitsOverPlanMessage}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Packaging comment row */}
                    <div className='grid grid-cols-[2.5rem_1fr] gap-4'>
                      <div />
                      <div className='flex flex-col gap-1'>
                        <span className='text-xs font-medium text-gray-500'>
                          Packaging comment{' '}
                          <span className='text-gray-400'>Optional</span>
                        </span>
                        <Controller
                          name={`skus.${index}.packagingComment`}
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              placeholder='Type here...'
                              className='w-full max-w-md px-3 py-1.5 text-sm border border-gray-200 rounded-md placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-200'
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Discrepancy reason row */}
                    {isDiscrepancy && (
                      <div className='grid grid-cols-[2.5rem_1fr] gap-4'>
                        <div />
                        <Controller
                          name={`skus.${index}.discrepancyReason`}
                          control={control}
                          render={({
                            field,
                            fieldState: { error, isTouched },
                          }) => {
                            return (
                              <Textarea
                                name={field.name}
                                label='Discrepancy reason'
                                optional={!isReasonRequired}
                                value={field.value ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                  field.onChange(event.target.value);
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                placeholder='Explain the discrepancy...'
                              />
                            );
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  /* Mobile/Tablet Layout */
                  <div className='flex gap-3'>
                    {/* Index */}
                    <div className='flex-shrink-0'>
                      <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                        {index + 1}
                      </span>
                    </div>
                    {/* Details */}
                    <div
                      className={`flex-1 flex flex-col gap-4 ${
                        isTablet ? '' : ''
                      }`}
                    >
                      <div
                        className={`grid gap-3 ${
                          isTablet ? 'grid-cols-2' : 'grid-cols-1'
                        }`}
                      >
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            SKU ID
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.skuIdentifier}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Product name
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.productName || skusField.skuName}
                          </span>
                          {packagingInfo ? (
                            <span className='text-xs text-gray-500'>
                              {packagingInfo.type}
                            </span>
                          ) : (
                            palletisingType && (
                              <span className='text-xs text-gray-500'>
                                {palletisingType}
                              </span>
                            )
                          )}
                          {isPlannedLine && (
                            <PlannedPickSummary
                              wholeUnitsToPick={skusField.wholeUnitsPicked ?? 0}
                              unitsToBreak={skusField.unitsBroken ?? 0}
                              looseToTake={skusField.looseTaken ?? 0}
                            />
                          )}
                          {isPlannedLine && (
                            <div className='mt-1'>{deviationToggle}</div>
                          )}
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Pallets
                          </span>
                          <div className='text-sm text-gray-900'>
                            {packagingInfo ? (
                              <PalletCalculator
                                palletName={packagingInfo.type}
                                skuQuantity={skuQuantity}
                                amountOfSkusInLayer={
                                  packagingInfo.amountOfSkusInLayer
                                }
                                amountOfLayersInPallet={
                                  packagingInfo.amountOfLayersInPallet
                                }
                              />
                            ) : isPlannedLine && hasBreakdown ? (
                              <PackagingBreakdown
                                packaging={skusField.packaging}
                                looseProducts={looseProducts}
                              />
                            ) : (
                              <span>{palletisingType}</span>
                            )}
                          </div>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            {quantityColumnLabel('Ordered', rowKind)}
                          </span>
                          {isPlannedLine ? (
                            <div className='flex flex-col'>
                              <span className='text-sm text-gray-900'>
                                {productsLabel(plannedProductsToShip)}
                              </span>
                              <span className='text-xs text-gray-500'>
                                {numberToLocale(skuQuantity)} whole
                              </span>
                            </div>
                          ) : (
                            <span className='text-sm text-gray-900'>
                              {skuLineQuantityLabel(skuQuantity, skusField)}
                            </span>
                          )}
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            {quantityColumnLabel('Picked', rowKind)}
                          </span>
                          {isPlannedLine ? (
                            <div className='flex flex-col'>
                              <span className='text-sm font-medium text-gray-900'>
                                {productsLabel(productsPicked ?? 0)}
                              </span>
                              <span className='text-xs text-gray-500'>
                                {numberToLocale(pickedQuantity ?? 0)} whole
                              </span>
                            </div>
                          ) : (
                            <div className='flex flex-col gap-1'>
                              <Controller
                                name={`skus.${index}.pickedQuantity`}
                                control={control}
                                render={({
                                  field,
                                  fieldState: { error, isTouched },
                                }) => {
                                  return (
                                    <Input
                                      name={field.name}
                                      type='number'
                                      value={field.value?.toString() ?? ''}
                                      onChange={(
                                        event: ChangeEvent<HTMLInputElement>
                                      ) => {
                                        const value = Number(
                                          event.target.value
                                        );
                                        if (Number.isNaN(value)) return;
                                        field.onChange(value);
                                        if (value === skuQuantity) {
                                          setValue(
                                            `skus.${index}.discrepancyReason`,
                                            null
                                          );
                                        }
                                      }}
                                      onBlur={field.onBlur}
                                      isInvalid={Boolean(error && isTouched)}
                                      validationMessage={error?.message}
                                      sizeVariant='sm'
                                    />
                                  );
                                }}
                              />
                              <span className='text-xs text-gray-500'>
                                {skuLineQuantityLabel(
                                  pickedQuantity ?? 0,
                                  skusField
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Break-pack capture */}
                      {isPlannedLine && showDeviation && (
                        <div
                          className={`grid gap-3 ${
                            isTablet ? 'grid-cols-2' : 'grid-cols-1'
                          }`}
                        >
                          <Controller
                            name={`skus.${index}.wholeUnitsPicked`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                label='Whole units picked'
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = Number(event.target.value);
                                  if (Number.isNaN(value)) return;
                                  field.onChange(value);
                                  applyBreakPackChange(
                                    value,
                                    unitsBroken ?? 0,
                                    looseTaken ?? 0
                                  );
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                          <Controller
                            name={`skus.${index}.unitsBroken`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                label='Units broken'
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = Number(event.target.value);
                                  if (Number.isNaN(value)) return;
                                  field.onChange(value);
                                  applyBreakPackChange(
                                    wholeUnitsPicked ?? 0,
                                    value,
                                    looseTaken ?? 0
                                  );
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                          <Controller
                            name={`skus.${index}.looseTaken`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                label='Loose taken'
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = Number(event.target.value);
                                  if (Number.isNaN(value)) return;
                                  field.onChange(value);
                                  applyBreakPackChange(
                                    wholeUnitsPicked ?? 0,
                                    unitsBroken ?? 0,
                                    value
                                  );
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                          <Controller
                            name={`skus.${index}.productsPicked`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                label='Products picked'
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const value = Number(event.target.value);
                                  if (Number.isNaN(value)) return;
                                  field.onChange(value);
                                  clearReasonWhenProductsMatchThePlan(value);
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                        </div>
                      )}
                      {showDeviation && plannedUnitsOverPlanMessage && (
                        <p className='text-sm text-red-600'>
                          {plannedUnitsOverPlanMessage}
                        </p>
                      )}

                      {/* Packaging comment */}
                      <div className='flex flex-col gap-1'>
                        <span className='text-xs font-medium text-gray-500'>
                          Packaging comment{' '}
                          <span className='text-gray-400'>Optional</span>
                        </span>
                        <Controller
                          name={`skus.${index}.packagingComment`}
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              placeholder='Type here...'
                              className='w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-200'
                            />
                          )}
                        />
                      </div>

                      {/* Discrepancy reason */}
                      {isDiscrepancy && (
                        <Controller
                          name={`skus.${index}.discrepancyReason`}
                          control={control}
                          render={({
                            field,
                            fieldState: { error, isTouched },
                          }) => {
                            return (
                              <Textarea
                                name={field.name}
                                label='Discrepancy reason'
                                optional={!isReasonRequired}
                                value={field.value ?? ''}
                                onChange={(
                                  event: ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                  field.onChange(event.target.value);
                                }}
                                onBlur={field.onBlur}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                placeholder='Explain the discrepancy...'
                              />
                            );
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comment Section */}
      <div className='px-6 py-4 border-t border-gray-200'>
        <Controller
          name='comment'
          control={control}
          render={({ field, fieldState: { error, isTouched } }) => (
            <Textarea
              {...field}
              value={field.value ?? ''}
              label='Comment'
              optional
              isInvalid={Boolean(error && isTouched)}
              validationMessage={error?.message}
              placeholder='Type here...'
            />
          )}
        />
        <div className='flex justify-end mt-1'>
          <span className='text-xs text-gray-400'>
            {watch('comment')?.length ?? 0}/500
          </span>
        </div>
      </div>

      {/* Footer Actions */}
      <div
        className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
          isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
        }`}
      >
        <Button
          label='Discard'
          intent='secondary'
          size='sm'
          onClick={onClose}
          disabled={isSubmitting}
          startIcon={<CloseIcon />}
          className={!isDesktop ? 'w-full' : ''}
          type='button'
        />
        <Button
          label={isSubmitting ? 'Confirming...' : 'Confirm'}
          intent='primary'
          size='sm'
          type='submit'
          disabled={isSubmitting || !isValid}
          className={!isDesktop ? 'w-full' : ''}
        />
      </div>
    </form>
  );
}
